import ProjectionProxy from "@/ui/map/ProjectionProxy";
import Location from "@/gis/Location";
import Point from "@/geom/Point";
import Size from "@/model/Size";
import OSMTile from "@/model/OSMTile";
import OSMUtils from "@/utils/OSMUtils";

export default class OndagoProjectionProxy extends ProjectionProxy {

    static #WORLD_ZOOM_ZERO;
	static #TILE_ZOOM_ZERO;
	
    constructor(projectionImpl, mapTileSize, mapBaseZoom, mapSize, worldTileSize) {
		super(projectionImpl);

		OndagoProjectionProxy.WORLD_ZOOM_ZERO = 10.0;
		OndagoProjectionProxy.TILE_ZOOM_ZERO = 10.0;

        this.mapTileSize = mapTileSize;
        this.mapBaseZoom = mapBaseZoom;
        this.mapSize = mapSize;
        this.worldTileSize = worldTileSize;
    }

	toWorldZoom(mapZoom) {
		return OndagoProjectionProxy.WORLD_ZOOM_ZERO + mapZoom;
	}
	
	toMapZoom(worldZoom) {
		return worldZoom - OndagoProjectionProxy.WORLD_ZOOM_ZERO;
	}
	
	toWorldOSMTile(mapTile) {
		if (mapTile == null) {
			return null;
		}

		const worldTopLeftTileForZoom = OSMUtils.fromLocation(new Location(0, 0), OndagoProjectionProxy.TILE_ZOOM_ZERO + mapTile.zoom);
		
		const newX = worldTopLeftTileForZoom.x + mapTile.x;
		const newY = worldTopLeftTileForZoom.y + mapTile.y;
		const worldTile = new OSMTile(newX, newY, worldTopLeftTileForZoom.zoom);
		return worldTile;
	}
	
	toMapOSMTile(worldOSMTile) {
		if (worldOSMTile == null) {
			return null;
		}

		const worldTopLeftTileForZoom = OSMUtils.fromLocation(new Location(0, 0), worldOSMTile.zoom);
		
		const mapX = worldOSMTile.x - worldTopLeftTileForZoom.x;
		const mapY = worldOSMTile.y - worldTopLeftTileForZoom.y;
		const mapTile = new OSMTile(mapX, mapY, worldOSMTile.zoom - OndagoProjectionProxy.TILE_ZOOM_ZERO);
		return mapTile;
	}
	
	toWorldLocation(mapLocationOrPoint, skipArea = false) {
		if (mapLocationOrPoint instanceof Location) {
			return this._toWorldLocationFromMapLocation(mapLocationOrPoint, skipArea);
		} else if (mapLocationOrPoint instanceof Point) {
			return this._toWorldLocationFromMapPoint(mapLocationOrPoint);
		} else {
			return null;
		}
	}

	_toWorldLocationFromMapLocation(mapLocation, skipArea) {
		if (mapLocation != null) {
			const mapPoint = this.projectionImpl.locationToPoint(mapLocation, skipArea);
			
			const tileX = mapPoint.x / this.mapTileSize;
			const tileY = mapPoint.y / this.mapTileSize;
			
			const xTile = Math.floor(tileX);
			const yTile = Math.floor(tileY);
			
			const reminderX = tileX - xTile;
			const reminderY = tileY - yTile;
			
			const containingTile = new OSMTile(xTile, yTile, this.mapBaseZoom);
			const bottomRightTile = new OSMTile(xTile + 1, yTile + 1, this.mapBaseZoom);
			
			const worldContainingTile = this.toWorldOSMTile(containingTile);
			const worldBottomRightTile = this.toWorldOSMTile(bottomRightTile);
			
			const tileLocation = OSMUtils.toLocationFromOSMTile(worldContainingTile);
			const bottomRightTileLocation = OSMUtils.toLocationFromOSMTile(worldBottomRightTile);
			
			const deltaLatitude = tileLocation.latitude - bottomRightTileLocation.latitude;
			const deltaLongitude = bottomRightTileLocation.longitude - tileLocation.longitude;
			
			const latitude = tileLocation.latitude - (deltaLatitude * reminderY);
			const longitude = tileLocation.longitude + (deltaLongitude * reminderX);
	
			return new Location(latitude, longitude);
		} else {
			return null;
		}
	}

	_toWorldLocationFromMapPoint(mapPoint) {
		const originWorldTile = OSMUtils.fromLocation(new Location(0, 0), this.toWorldZoom(this.mapBaseZoom));
		
		const xInTiles = mapPoint.x / this.worldTileSize;
		const yInTiles = mapPoint.y / this.worldTileSize;
		
		const tileX = originWorldTile.x + Math.floor(xInTiles);
		const tileY = originWorldTile.y + Math.floor(yInTiles);
		
		const reminderX = xInTiles - Math.floor(xInTiles);
		const reminderY = yInTiles - Math.floor(yInTiles);
		
		const tile = new OSMTile(tileX, tileY, this.toWorldZoom(this.mapBaseZoom));
		const bottomRightTile = new OSMTile(tileX + 1, tileY + 1, this.toWorldZoom(this.mapBaseZoom));
		
		const tileLocation = OSMUtils.toLocationFromOSMTile(tile);
		const bottomRightTileLocation = OSMUtils.toLocationFromOSMTile(bottomRightTile);
		
		const deltaLatitude = tileLocation.latitude - bottomRightTileLocation.latitude;
		const deltaLongitude = bottomRightTileLocation.longitude - tileLocation.longitude;
		
		const latitude = tileLocation.latitude - (deltaLatitude * reminderY);
		const longitude = tileLocation.longitude + (deltaLongitude * reminderX);
		return new Location(latitude, longitude);
	}

	toMapLocation(worldLocation) {
		if (worldLocation != null) {
			const worldContainingTile = OSMUtils.fromLocation(worldLocation, this.toWorldZoom(this.mapBaseZoom));
			const worldBottomRightTile = new OSMTile(worldContainingTile.x + 1, worldContainingTile.y + 1, worldContainingTile.zoom);

			const containingWorldTileLocation = OSMUtils.toLocationFromOSMTile(worldContainingTile);
			const bottomRightWorldTileLocation = OSMUtils.toLocationFromOSMTile(worldBottomRightTile);
			
			const tileHeightInLatitude = containingWorldTileLocation.latitude - bottomRightWorldTileLocation.latitude;
			const tileHeightInLongitude = bottomRightWorldTileLocation.longitude - containingWorldTileLocation.longitude;
			
			const latitudePercent = 1 - ((worldLocation.latitude - bottomRightWorldTileLocation.latitude) / tileHeightInLatitude);
			const longitudePercent = 1 - ((bottomRightWorldTileLocation.longitude - worldLocation.longitude) / tileHeightInLongitude);
			
			const containingTile = this.toMapOSMTile(worldContainingTile);

			const x = (containingTile.x * this.mapTileSize) + (this.mapTileSize * longitudePercent);
			const y = (containingTile.y * this.mapTileSize) + (this.mapTileSize * latitudePercent);
			
			const mapLocation = this.projectionImpl.pointToLocation(new Point(x, y));
			return mapLocation;
		} else {
			return null;
		}
	}
	
	canIndexByTile() {
		return false;
	}
	
	getWorldTopLeftCornerForMap(mapTopLeftCorner, worldZoom, screenSizeInPoints) {
		const worldBaseZoom = this.toWorldZoom(this.mapBaseZoom);
		
		var magnifier = 1.0;
		const zoomDiff = worldZoom - (worldBaseZoom - 1);
		if (zoomDiff > 0.0) {
			magnifier = Math.pow(2.0, zoomDiff);
		} else if (zoomDiff < 0.0) {
			magnifier = 1 / Math.pow(2.0, Math.abs(zoomDiff));
		}
		
		const scaledScreenSize = new Size({ width: screenSizeInPoints.width / magnifier, height: screenSizeInPoints.height / magnifier });
		
		const topLeftPoint = new Point(scaledScreenSize.width / 2.0, scaledScreenSize.height / 2.0);
		
		return this.toWorldLocation(topLeftPoint);
	}
	
	getWorldBottomRightCornerForMap(mapBottomRightCorner, worldZoom, screenSizeInPoints) {
		const worldBaseZoom = this.toWorldZoom(this.mapBaseZoom);
		
		var magnifier = 1.0;
		const zoomDiff = worldZoom - (worldBaseZoom - 1);
		if (zoomDiff > 0.0) {
			magnifier = Math.pow(2.0, zoomDiff);
		} else if (zoomDiff < 0.0) {
			magnifier = 1 / Math.pow(2.0, Math.abs(zoomDiff));
		}
		
		const scaledScreenSize = new Size({ width: screenSizeInPoints.width / magnifier, height: screenSizeInPoints.height / magnifier });
		
		const bottomRightPoint = new Point(this.mapSize.width - (scaledScreenSize.width / 2), this.mapSize.height - (scaledScreenSize.height / 2.0));
		
		return this.toWorldLocation(bottomRightPoint);
	}
}